import FxxlCore from '@fxxl/core';
import FxxlError from '@fxxl/error';

export default class FxxlShopSuggestion {

    /**
     * Konstruktor
     *
     * @param device string
     */
    constructor(device = 'desktop') {
        this.device = device;
        this.targetCountry = null;
        this.currentCountry = null;
        this.countries = [];
        this.geoCountry = null;
        this.alternateLinks = [];
        this.alternateLinksDoms = [];
        this.alternateLink = null;
        this.popupModal = null;
        this.getGeoIpIso2CodeUrl = '/json.php?service=getGeoIpIso2Code';
        this.fxxlCore = new FxxlCore();
        this.alternateLinksMapping = {
                'de': 'de-de',
                'at': 'de-at',
        };

        // Prüfen ob IP Detection Popup deaktiviert ist
        if (+fxxl_config_from_php?.presence?.variables?.FXXL_DISABLE_IP_DETECTION_POPUP !== 1) {
            this.init();
        }
    }

    /**
     * Initialisierung
     */
    async init() {
        // Länder holen
        if (window?.fxxl_config_from_php?.shop_suggestion_countries) {
            this.countries = window.fxxl_config_from_php.shop_suggestion_countries;
        }

        // GeoIP Land holen
        await this.getGeoIpIso2Code();

        // Alternativ Links holen
        this.alternateLinksDoms = document.querySelectorAll('link[rel="alternate"]');

        // Prüfen ob Empfehlung angezeigt werden soll
        this.check();
    }

    /**
     * GeoIP Land holen
     *
     * @returns {Promise<void>}
     */
    async getGeoIpIso2Code() {
        try {
            const response = await fetch(this.getGeoIpIso2CodeUrl);
            const data = await response.json();

            if (data?.status === 'success' && data?.code) {
                this.geoCountry = data.code;
            } else {
                new FxxlError('Failed to fetch country code');
            }
        } catch (error) {
            new FxxlError(error);
        }
    }

    /**
     * Prüfen ob Land zu aktuellem Presence gehört
     *
     * @param country
     * @returns {boolean}
     */
    isCurrentCountry(country) {
        return +country.c_presence_id === +_presence_id
    }

    /**
     * Prüfen ob Empfehlung angezeigt werden soll
     */
    check() {
        if (this.countries.length > 0 && this.geoCountry) {
            this.countries.forEach((country) => {
                if (country?.iso_code_2.toUpperCase() === this.geoCountry.toUpperCase()) {
                    // Prüfen ob Land in Mapping vorhanden ist
                    if (this.alternateLinksMapping[country.iso_code_2.toLowerCase()]) {
                        this.targetCountry = country;
                    }
                }

                if (this.isCurrentCountry(country)) {
                    this.currentCountry = country;
                }
            });
        }

        if (this.targetCountry !== null && !this.isCurrentCountry(this.targetCountry)) {
            this.doSuggestions(this.targetCountry);
        } else if (this.targetCountry === null) {
            FxxlShopSuggestion.setStorageSelectedSuggestedCountry(this.currentCountry.iso_code_2);
            new FxxlError('Target country not found');
        } else {
            // Wenn bereits im korrektem Land
            FxxlShopSuggestion.setStorageSelectedSuggestedCountry(this.currentCountry.iso_code_2);
        }
    }

    /**
     * Alternativ Links setzen
     */
    setAlternateLinks() {
        this.alternateLinksDoms.forEach((link) => {
            this.alternateLinks[link.getAttribute('hreflang')] = link.getAttribute('href');
        });
    }

    /**
     * Vorschlag anzeigen
     *
     * @param suggestionCountry string
     */
    doSuggestions(suggestionCountry) {
        this.setAlternateLinks();
        let alternateLink = this.alternateLinks[this.alternateLinksMapping[suggestionCountry.iso_code_2.toLowerCase()]];

        if (!alternateLink) {
            alternateLink = suggestionCountry.url;
        }

        if (alternateLink) {
            this.alternateLink = this.prepareAlternateLinks(alternateLink);
            this.showPopup();
        }
    }

    /**
     * Vorbereitung der Alternativ Links
     *
     * @param link string
     * @returns {string}
     */
    prepareAlternateLinks(link) {
        const url = new URL(link.trim());
        const utmSource = this.fxxlCore.getParameterByName('utm_source');
        const utmMedium = this.fxxlCore.getParameterByName('utm_medium');
        const utmCampaign = this.fxxlCore.getParameterByName('utm_campaign');
        const gadSource = this.fxxlCore.getParameterByName('gad_source');
        const referrer = document.referrer;
        const currentIso2 = this.currentCountry.iso_code_2.toLowerCase();
        const cookieAccepted = +(etJS_getCookie('fxxl_optional_cookies') === 'true');

        url.searchParams.set('redirected_from_other_language', cookieAccepted);
        url.searchParams.set('utm_content', currentIso2);

        if (utmSource && utmMedium && utmCampaign) {
            url.searchParams.set('utm_source', utmSource);
            url.searchParams.set('utm_medium', utmMedium);
            url.searchParams.set('utm_campaign', utmCampaign);
        } else if (gadSource) {
            url.searchParams.set('gad_source', 'google');
            url.searchParams.set('utm_medium', 'cpc');
        } else if (referrer.indexOf('.google.com') !== -1) {
            url.searchParams.set('utm_source', 'google');
            url.searchParams.set('utm_medium', 'organic');
        }

        return url.toString();
    }

    createSuggestionsPopup() {
        // Modal Container
        const modal = document.createElement('div');
        modal.className = 'fxxl-modal modal';
        if (this.device === 'mobile') {
            modal.classList.add('bottom-sheet');
        }

        // Modal Header
        const modalHeader = document.createElement('div');
        modalHeader.className = 'fxxl-modal__header modal-header';

        // Modal Header Text
        const modalHeaderText = document.createElement('div');
        modalHeaderText.className = 'fxxl-modal__header-text';
        modalHeaderText.textContent = window?.fxxl_translate_texts?.shopSuggestion?.headerText.replace('###COUNTRY###', this.targetCountry.description);

        const modalHeaderClose = document.createElement('a');
        modalHeaderClose.className = 'fxxl-modal__header-close modal-close';
        modalHeaderClose.innerHTML = '<svg width="24" height="24"><use href="#sprite_icon_close" xlink:href="#sprite_icon_close"></use></svg>';

        // Modal Content
        const modalContent = document.createElement('div');
        modalContent.className = 'fxxl-modal__content modal-content fxxl-shop-suggestion-popup';
        // Modal Content Text
        const modalContentText = document.createElement('div');
        modalContentText.className = 'fxxl-shop-suggestion-popup__text';
        modalContentText.textContent = window?.fxxl_translate_texts?.shopSuggestion?.contentText ;
        // Shop suggestion buttons
        const modalShopSuggestionButtons = document.createElement('div');
        modalShopSuggestionButtons.className = 'fxxl-shop-suggestion-popup__buttons';
        // Add to modal content
        modalContent.appendChild(modalContentText);
        modalContent.appendChild(modalShopSuggestionButtons);

        // Add buttons
        for (const [key, value] of Object.entries(this.alternateLinksMapping)) {
            const button = this.createButton(key);
            if (this.targetCountry.iso_code_2.toLowerCase() === key) {
                button.classList.add('fxxl-shop-suggestion-popup__button--distance');
                button.href = this.alternateLink;
                button.style.order = 1;
            } else {
                button.classList.add('modal-close');
                button.setAttribute('data-cy', 'shop-suggestion-popup-button-close');
            }
            modalShopSuggestionButtons.appendChild(button);
        }


        modalHeader.appendChild(modalHeaderText);
        modalHeader.appendChild(modalHeaderClose);

        modal.appendChild(modalHeader);
        modal.appendChild(modalContent);

        this.popupModal = modal;
    }

    /**
     * Erstellt einen Button für das Land
     *
     * @param country string
     * @returns {HTMLAnchorElement}
     */
    createButton(country) {
        const deButton = document.createElement('a');
        deButton.className = 'fxxl-button fxxl-shop-suggestion-popup__button';

        // IMG element
        const deImgButton = document.createElement('img');
        deImgButton.setAttribute('width', '24');
        deImgButton.setAttribute('height', '24');
        deImgButton.setAttribute('class', 'fxxl-button__icon fxxl-button__icon--left');
        deImgButton.src = `/custom/module/public/fxxl3/assets/img/icons/flags/flag_of_${country}.svg`;

        // Button text element
        const deTextButton = document.createElement('span');
        deTextButton.className = 'fxxl-button__text';
        deTextButton.textContent = window?.fxxl_translate_texts?.shopSuggestion?.countries[country]?.title;
        deButton.appendChild(deImgButton);
        deButton.appendChild(deTextButton);

        return deButton;
    }

    showPopup() {
        this.createSuggestionsPopup();
        document.body.appendChild(this.popupModal);

        setTimeout(() => {
            const instance = M.Modal.init(this.popupModal, {
                onCloseEnd: () => {
                    FxxlShopSuggestion.setStorageSelectedSuggestedCountry(this.currentCountry?.iso_code_2);
                }
            });
            this.setPopupModalSize();
            instance.open();
        }, 100);
    }

    setPopupModalSize() {
        if (this.device === 'desktop') {
            this.popupModal.style.maxWidth = '600px';
        }
    }

    static setStorageSelectedSuggestedCountry(selectedCountryISO) {
        const storageKey = FxxlCore.getConfig()?.storageKeys?.shopSuggestion;

        const data = FxxlCore.getStorage(storageKey) || {};

        if (!selectedCountryISO) {
            selectedCountryISO = _langcode;
        }

        if (selectedCountryISO) {
            data.selectedCountryISO = selectedCountryISO.toLowerCase();
            FxxlCore.setStorage(storageKey, data);
        }
    }

    static isSetStorageSelectedSuggestedCountry() {
        const storageKeyShopSuggestion = FxxlCore.getConfig()?.storageKeys?.shopSuggestion;
        const storageDataShopSuggestion = FxxlCore.getStorage(storageKeyShopSuggestion);
        const selectedCountry = storageDataShopSuggestion?.selectedCountryISO;

        return typeof selectedCountry === 'string' && selectedCountry !== '';
    }
}